import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';

interface Resource {
  id: string;
  title: string;
  category: string;
  description: string;
  type: 'template' | 'tutorial' | 'tool' | 'example';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  downloadUrl?: string;
  externalUrl?: string;
}

const ResourceCenterPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const resources: Resource[] = [
    {
      id: '1',
      title: '都市爱情剧本模板',
      category: 'templates',
      description: '专业的都市爱情题材剧本模板，包含标准格式和情节框架',
      type: 'template',
      difficulty: 'beginner',
      downloadUrl: '/templates/urban-love-template.docx'
    },
    {
      id: '2',
      title: '短视频拍摄入门教程',
      category: 'tutorials',
      description: '从零开始学习短视频拍摄技巧，包含构图、光线、运镜等基础知识',
      type: 'tutorial',
      difficulty: 'beginner'
    },
    {
      id: '3',
      title: '剧本写作工具集',
      category: 'tools',
      description: '专业的剧本写作软件推荐和使用指南，提升创作效率',
      type: 'tool',
      difficulty: 'intermediate'
    },
    {
      id: '4',
      title: '成功案例分析',
      category: 'examples',
      description: '分析热门短剧的成功要素，学习优秀创作经验',
      type: 'example',
      difficulty: 'intermediate'
    },
    {
      id: '5',
      title: '古装剧创作指南',
      category: 'templates',
      description: '古装题材的创作要点和历史背景参考',
      type: 'template',
      difficulty: 'advanced'
    },
    {
      id: '6',
      title: 'AI配音工具使用教程',
      category: 'tutorials',
      description: '学习使用AI工具为短剧配音，制作专业音效',
      type: 'tutorial',
      difficulty: 'intermediate'
    }
  ];

  const categories = [
    { id: 'all', name: '全部资源', count: resources.length },
    { id: 'templates', name: '剧本模板', count: resources.filter(r => r.category === 'templates').length },
    { id: 'tutorials', name: '教程指南', count: resources.filter(r => r.category === 'tutorials').length },
    { id: 'tools', name: '创作工具', count: resources.filter(r => r.category === 'tools').length },
    { id: 'examples', name: '案例分析', count: resources.filter(r => r.category === 'examples').length }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'template': return '📋';
      case 'tutorial': return '📚';
      case 'tool': return '🔧';
      case 'example': return '🎬';
      default: return '📄';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100';
      case 'intermediate': return 'text-yellow-600 bg-yellow-100';
      case 'advanced': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return '入门';
      case 'intermediate': return '中级';
      case 'advanced': return '高级';
      default: return '未知';
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 面包屑导航 */}
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">资源中心</span>
        </nav>

        {/* 页面标题 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">资源中心</h1>
          <p className="text-gray-600">丰富的创作资源和工具，助力您的短剧创作之路</p>
        </div>

        {/* 搜索栏 */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜索资源..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* 分类筛选 */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>

        {/* 资源列表 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource) => (
            <div key={resource.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="text-3xl">{getTypeIcon(resource.type)}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{resource.title}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getDifficultyColor(resource.difficulty)}`}>
                      {getDifficultyText(resource.difficulty)}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{resource.description}</p>

              <div className="flex space-x-2">
                {resource.downloadUrl && (
                  <button
                    onClick={() => alert(`正在下载: ${resource.title}`)}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    📥 下载
                  </button>
                )}
                {resource.externalUrl && (
                  <button
                    onClick={() => alert(`正在打开: ${resource.title}\n\n功能开发中，敬请期待！`)}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    🔗 查看详情
                  </button>
                )}
                {!resource.downloadUrl && !resource.externalUrl && (
                  <button
                    onClick={() => alert(`正在查看: ${resource.title}\n\n内容详情:\n${resource.description}\n\n难度等级: ${getDifficultyText(resource.difficulty)}\n类型: ${getTypeIcon(resource.type)} ${resource.type === 'template' ? '模板' : resource.type === 'tutorial' ? '教程' : resource.type === 'tool' ? '工具' : '案例'}`)}
                    className="flex-1 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    📖 查看内容
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 热门推荐 */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">热门推荐</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center text-white text-2xl">
                🔥
              </div>
              <div>
                <h4 className="font-medium text-gray-900">新手入门必看</h4>
                <p className="text-sm text-gray-600">从零开始学习短剧创作的完整指南</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-lg flex items-center justify-center text-white text-2xl">
                ⭐
              </div>
              <div>
                <h4 className="font-medium text-gray-900">专业工具推荐</h4>
                <p className="text-sm text-gray-600">提升创作效率的专业软件工具集</p>
              </div>
            </div>
          </div>
        </div>

        {/* 学习路径 */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">推荐学习路径</h3>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">基础入门</h4>
                <p className="text-sm text-gray-600">了解短剧基本概念和创作流程</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">技能提升</h4>
                <p className="text-sm text-gray-600">学习剧本写作、拍摄技巧等专业技能</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900">实战创作</h4>
                <p className="text-sm text-gray-600">通过实际项目练习提升创作能力</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceCenterPage;